/**
 * @file apps/web/src/components/ProcessingStatus.tsx
 * @description Polling component that tracks an audio upload pipeline through
 * its stages. Calls GET /audio/:uploadId every 2s until it reaches a terminal
 * stage — 'done', 'error' (whole pipeline failed, nothing saved), or
 * 'requirements_error' (transcript saved, but requirements extraction/
 * Discovery matching failed — see uploadPipeline.ts's
 * extractRequirementsAndSuggestDiscovery). Shows animated step indicators
 * matching the UploadZone pipeline stages.
 */

import { useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PipelineStage =
  | 'uploaded'
  | 'transcribing'
  | 'extracting'
  | 'matching'
  | 'requirements_error'
  | 'done'
  | 'error';

export interface UploadStatus {
  uploadId:                     string;
  stage:                        PipelineStage;
  transcriptionId?:             string | null;
  transcriptText?:              string | null;
  requirementsExtracted?:       number | null;
  discoverySuggestionsCreated?: number | null;
  gapsCreated?:                 number | null;
  durationSecs?:                number;
  language?:                    string;
  errorMessage?:                string | null;
}

interface ProcessingStatusProps {
  uploadId:    string;
  pollFn:      (uploadId: string) => Promise<UploadStatus>;
  onComplete?: (status: UploadStatus) => void;
  onError?:    (err: string) => void;
  // Optional — when given, the "done" summary links to that project's
  // Discovery page (where accepted suggestions show up) instead of just
  // reporting counts with nowhere to go.
  projectId?:  string;
  // Optional — when given, the "done" summary's review link becomes an in-page
  // action (the caller closes the panel and brings the suggestions into view)
  // instead of a full-page navigation to the same Discovery route.
  onReviewSuggestions?: () => void;
  className?:  string;
}

// ---------------------------------------------------------------------------
// Pipeline step definitions
// ---------------------------------------------------------------------------

interface PipelineStep {
  key:          PipelineStage;
  label:        string;
  description:  string;
}

const STEPS: PipelineStep[] = [
  {
    key:         'uploaded',
    label:       'File uploaded',
    description: 'Audio stored and queued for transcription.',
  },
  {
    key:         'transcribing',
    label:       'Transcribing with Whisper',
    description: 'Converting speech to text. This may take a moment.',
  },
  {
    key:         'extracting',
    label:       'Extracting requirements',
    description: 'Claude is identifying business requirements from the transcript.',
  },
  {
    key:         'matching',
    label:       'Matching to Discovery',
    description: 'Comparing requirements against the NetSuite rule database and suggesting Discovery dispositions.',
  },
  {
    key:         'done',
    label:       'Pipeline complete',
    description: 'Transcript saved and Discovery suggestions posted for review.',
  },
];

const STAGE_ORDER: Record<PipelineStage, number> = {
  uploaded:    0,
  transcribing: 1,
  extracting:  2,
  matching:    3,
  done:        4,
  error:       -1,
  // Terminal, but not a whole-pipeline failure — the transcript itself
  // succeeded (transcribing/uploaded stay 'done'). Placed at 'extracting'
  // (order 2) since that's the step most likely to have actually failed —
  // see StepIndicator rendering below for how this and 'error' differ.
  requirements_error: 2,
};

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

function StepIndicator({
  step,
  status,
}: {
  step:   PipelineStep;
  status: 'done' | 'active' | 'pending' | 'error';
}) {
  return (
    <div className="flex items-start gap-3">
      {/* Icon */}
      <div className={[
        'w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5',
        status === 'done'    ? 'bg-success/12' : '',
        status === 'active'  ? 'bg-blue/10'    : '',
        status === 'pending' ? 'bg-gray'        : '',
        status === 'error'   ? 'bg-danger/10'   : '',
      ].join(' ')}>
        {status === 'done'  && <CheckCircle size={14} className="text-success" />}
        {status === 'active' && <Loader2 size={14} className="text-blue animate-spin" />}
        {status === 'pending' && <span className="w-2 h-2 rounded-full bg-muted/30" />}
        {status === 'error' && <AlertCircle size={14} className="text-danger" />}
      </div>

      {/* Label + description */}
      <div className="flex-1 min-w-0 pb-5">
        <p className={[
          'text-sm font-medium leading-none',
          status === 'done'    ? 'text-success'  : '',
          status === 'active'  ? 'text-blue'     : '',
          status === 'pending' ? 'text-muted'    : '',
          status === 'error'   ? 'text-danger'   : '',
        ].join(' ')}>
          {step.label}
        </p>
        {(status === 'active' || status === 'done') && (
          <p className="text-xs text-muted mt-0.5">{step.description}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result summary card
// ---------------------------------------------------------------------------

function ResultSummary({ status, projectId, onReviewSuggestions }: { status: UploadStatus; projectId?: string; onReviewSuggestions?: () => void }) {
  if (status.stage !== 'done') return null;

  const hasSuggestions = (status.discoverySuggestionsCreated ?? 0) > 0;

  return (
    <div className="mt-4 p-4 bg-success/8 rounded-xl border border-success/20 space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle size={16} className="text-success shrink-0" />
        <p className="text-sm font-semibold text-success">Pipeline complete!</p>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] font-mono text-ink/70 mt-1">
        {status.language && (
          <p>Language: <span className="font-semibold text-ink">{status.language.toUpperCase()}</span></p>
        )}
        {status.durationSecs != null && (
          <p>
            Duration:{' '}
            <span className="font-semibold text-ink">
              {Math.floor(status.durationSecs / 60)}m {status.durationSecs % 60}s
            </span>
          </p>
        )}
        {status.requirementsExtracted != null && (
          <p>Requirements found: <span className="font-semibold text-ink">{status.requirementsExtracted}</span></p>
        )}
        {status.discoverySuggestionsCreated != null && (
          <p>Discovery suggestions: <span className="font-semibold text-ink">{status.discoverySuggestionsCreated}</span></p>
        )}
        {status.gapsCreated != null && status.gapsCreated > 0 && (
          <p>Gaps detected: <span className="font-semibold text-ink">{status.gapsCreated}</span></p>
        )}
      </div>
      {hasSuggestions && (
        <p className="text-xs text-ink/70">
          Suggestions are advisory — nothing was set automatically. Review and accept them on the Discovery page.
        </p>
      )}
      {hasSuggestions && onReviewSuggestions && (
        <button
          type="button"
          onClick={onReviewSuggestions}
          className="inline-flex items-center gap-1.5 text-xs text-blue font-medium hover:underline mt-1"
        >
          Review suggestions <ExternalLink size={11} />
        </button>
      )}
      {hasSuggestions && !onReviewSuggestions && projectId && (
        <a
          href={`/projects/${projectId}/discovery`}
          className="inline-flex items-center gap-1.5 text-xs text-blue font-medium hover:underline mt-1"
        >
          Review suggestions in Discovery <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProcessingStatus
// ---------------------------------------------------------------------------

export function ProcessingStatus({
  uploadId,
  pollFn,
  onComplete,
  onError,
  projectId,
  onReviewSuggestions,
  className = '',
}: ProcessingStatusProps) {
  const [status, setStatus]       = useState<UploadStatus | null>(null);
  const [pollError, setPollError] = useState<string>('');
  const timerRef                  = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await pollFn(uploadId);
        if (cancelled) return;
        setStatus(result);
        setPollError('');

        if (result.stage === 'done') {
          if (timerRef.current) clearInterval(timerRef.current);
          onComplete?.(result);
        } else if (result.stage === 'error') {
          if (timerRef.current) clearInterval(timerRef.current);
          const msg = result.errorMessage ?? 'Pipeline failed';
          setPollError(msg);
          onError?.(msg);
        } else if (result.stage === 'requirements_error') {
          // Terminal (nothing more will happen), but not a whole-pipeline
          // failure — the transcript succeeded and is saved. Stop polling;
          // don't route through setPollError/onError, which the 'error'
          // banner below reads — this gets its own, less alarming banner.
          if (timerRef.current) clearInterval(timerRef.current);
        }
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to fetch status';
        setPollError(msg);
        if (timerRef.current) clearInterval(timerRef.current);
        onError?.(msg);
      }
    };

    // Poll immediately, then every 2s
    poll();
    timerRef.current = setInterval(poll, 2000);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [uploadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStage         = status?.stage ?? 'uploaded';
  const currentOrder         = STAGE_ORDER[currentStage];
  const isError              = currentStage === 'error';
  const isRequirementsError  = currentStage === 'requirements_error';

  return (
    <div className={['space-y-1', className].join(' ')}>
      {/* Upload ID pill */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-mono text-muted">
          Upload ID: <span className="text-ink">{uploadId.slice(0, 8)}…</span>
        </p>
        {currentStage !== 'done' && currentStage !== 'error' && currentStage !== 'requirements_error' && (
          <span className="flex items-center gap-1 text-[11px] font-mono text-muted animate-pulse">
            <Loader2 size={10} className="animate-spin" />
            Processing…
          </span>
        )}
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Vertical connector line */}
        <div className="absolute left-3.5 top-7 bottom-5 w-px bg-[rgba(8,17,31,0.07)]" />

        {STEPS.map((step) => {
          const order = STAGE_ORDER[step.key];
          let stepStatus: 'done' | 'active' | 'pending' | 'error' = 'pending';

          if (isError) {
            stepStatus = currentOrder >= 0 && order <= currentOrder ? 'done' : 'pending';
          } else if (isRequirementsError) {
            stepStatus = order < currentOrder ? 'done' : order === currentOrder ? 'error' : 'pending';
          } else if (order < currentOrder) {
            stepStatus = 'done';
          } else if (order === currentOrder) {
            stepStatus = currentStage === 'done' ? 'done' : 'active';
          }

          return (
            <StepIndicator
              key={step.key}
              step={step}
              status={stepStatus}
            />
          );
        })}
      </div>

      {/* Error state — whole pipeline failed, nothing was saved */}
      {(isError || pollError) && (
        <div className="flex items-start gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mt-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Pipeline failed</p>
            <p className="text-xs mt-0.5 text-danger/80">{status?.errorMessage ?? pollError}</p>
          </div>
        </div>
      )}

      {/* Requirements extraction/Discovery matching failed — distinct from
          the above: the transcript itself succeeded and is saved, only the
          requirements/suggestions step didn't complete. Amber, not red, and
          different copy, so this doesn't read as having lost the upload. */}
      {isRequirementsError && (
        <div className="flex items-start gap-2 p-3 bg-warning/8 rounded-lg text-warning text-sm mt-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Transcript saved, but requirements extraction failed</p>
            <p className="text-xs mt-0.5 text-warning/80">
              {status?.errorMessage ?? 'Requirements extraction failed.'}
            </p>
            <p className="text-xs mt-1 text-ink/60">
              No Discovery suggestions were generated for this recording. The transcript itself is unaffected — try re-uploading if you need suggestions.
            </p>
          </div>
        </div>
      )}

      {/* Done summary */}
      {status && <ResultSummary status={status} projectId={projectId} onReviewSuggestions={onReviewSuggestions} />}
    </div>
  );
}
